import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Alert,
} from "@mui/material";
import AsyncSearchDropdown from "./AsyncSearchDropdown";
import { project_dropdown_url, layer_dropdown_url , user_dropdown_url} from "../api_registry";
import {create_ticket_url} from "../api_registry";

export default function CreateTicketDialog({ open, onClose }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const [project, setProject] = useState(null);
    const [ticket, setTicket] = useState(null);
    const [additionalLayers, setAdditionalLayers] = useState([]);
    const [assignedBy, setAssignedBy] = useState(null);
    const [assignedTo, setAssignedTo] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleCreate = async () => {
        setError(null);

        // basic frontend validation
        if (!name || !project || !ticket || !assignedTo) {
            setError("Please fill all required fields");
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(
                create_ticket_url,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        name,
                        description,
                        assigned_to: assignedTo.id,
                        project_id: project.id,
                        route_layer_id: ticket.id,
                        // arrays left empty for now as requested
                        route_segment_ids: [],
                        additional_layer_ids: additionalLayers.map((l) => l.id),
                    }),
                }
            );

            const data = await res.json();

            if (!res.ok) {
                // backend error message handling
                throw new Error(
                    data?.detail ||
                    data?.message ||
                    "Failed to create ticket"
                );
            }

            // success
            onClose();
            resetForm();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setName("");
        setDescription("");
        setProject(null);
        setTicket(null);
        setAssignedBy(null);
        setAssignedTo(null);
        setError(null);
    };

    // recompute layer API url base on selected project. the dropdown helper
    // will append paging/search params and handles whether to use "?" or "&".
    const layerApiUrl = project
        ? `${layer_dropdown_url}?project_id=${project.id}`
        : layer_dropdown_url;

    // clear layer and additional selections when project changes
    useEffect(() => {
        setTicket(null);
        setAdditionalLayers([]);
    }, [project]);

    // ensure route layer isn't available/selected as an additional layer
    useEffect(() => {
        if (ticket) {
            setAdditionalLayers((prev) =>
                prev.filter((l) => l.id !== ticket.id)
            );
        }
    }, [ticket]);

    return (
        <Dialog open={open} onClose={() => !loading && onClose()} fullWidth maxWidth="sm">
            <DialogTitle>Create Ticket</DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    margin="dense"
                    label="Ticket Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                />

                <TextField
                    fullWidth
                    margin="dense"
                    label="Description"
                    multiline
                    minRows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={loading}
                />

                <AsyncSearchDropdown
                    label="Project"
                    apiUrl={project_dropdown_url}
                    value={project}
                    onChange={setProject}
                />

                <AsyncSearchDropdown
                    label="Route layer"
                    apiUrl={layerApiUrl}
                    value={ticket}
                    onChange={setTicket}
                    disabled={!project}
                />

                <AsyncSearchDropdown
                    label="Additional layers"
                    apiUrl={layerApiUrl}
                    value={additionalLayers}
                    onChange={setAdditionalLayers}
                    multiple
                    disabled={!project}
                    filterOptions={(opts) =>
                        opts.filter((o) => o.id !== ticket?.id)
                    }
                />

                <AsyncSearchDropdown
                    label="Assignee"
                    apiUrl={user_dropdown_url}
                    value={assignedTo}
                    onChange={setAssignedTo}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
